import React, { useState } from 'react'
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  TooltipItem
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import moment from 'moment-timezone'
import 'chartjs-adapter-moment'
import axios from 'axios'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { LocalizationContextType } from '../../shared/contexts'
import { toDisplayTemperature, temperatureUnit } from '../../../utils/formatTemperature'
import { trend_admin_robot_sensors_path } from '../../../../routes'

// Legend is rendered as HTML below, so Chart.js's own Legend plugin is not registered.
ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Tooltip)

const SERIES_COLORS = [
  '#4285F4', '#34A853', '#EA4335', '#FBBC05', '#9C27B0', '#00ACC1',
  '#FF7043', '#5E35B1', '#43A047', '#D81B60', '#3949AB', '#00897B'
]

// `Number(...toFixed(2))` drops binary-float tails (4.000000000000001 -> 4)
// without padding whole numbers with decimals.
const roundTemperature = (value: number | string): number => {
  const numeric = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : 0
}

interface TrendReading {
  recorded_at: string
  recorded_value: number
}

interface TrendSeries {
  sensor_id: string
  sensor_name: string
  readings: TrendReading[]
}

interface TrendData {
  series: TrendSeries[]
  interval_minutes: number
  readings_count: number
  sensors_count: number
  date: string
  max_date: string
  starts_at: string
  ends_at: string
}

const DATE_FORMAT = 'YYYY-MM-DD'

interface SensorTrendChartProps {
  trend: TrendData
  localization_context?: LocalizationContextType
}

const SensorTrendChart: React.FC<SensorTrendChartProps> = ({
  trend: initialTrend,
  localization_context: localizationContext
}) => {
  const [trend, setTrend] = useState<TrendData>(initialTrend)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hiddenSensorIds, setHiddenSensorIds] = useState<string[]>([])
  const timezone = localizationContext?.timezone || 'UTC'
  const unit = temperatureUnit(localizationContext)

  const isToday = trend.date === trend.max_date

  // The picker's value is local state: binding it straight to the loaded trend
  // would rewrite the field from the server response while the user is still
  // typing a date, corrupting the rest of their input.
  const [selectedDay, setSelectedDay] = useState<moment.Moment>(moment(initialTrend.date, DATE_FORMAT))

  // Nothing is recorded after today, so the picker cannot go past it.
  const loadDay = (date: moment.Moment | null) => {
    if (date === null || !date.isValid() || loading) return

    const day = date.format(DATE_FORMAT)
    if (day === trend.date || day > trend.max_date) return

    setLoading(true)
    setError(null)
    axios
      .get(trend_admin_robot_sensors_path(), { params: { date: day } })
      .then(({ data }) => setTrend(data))
      .catch(() => setError('Could not load that day.'))
      .finally(() => setLoading(false))
  }

  const toggleSensor = (sensorId: string) => {
    setHiddenSensorIds((hidden) =>
      hidden.includes(sensorId) ? hidden.filter((id) => id !== sensorId) : [...hidden, sensorId]
    )
  }

  const colorFor = (index: number) => SERIES_COLORS[index % SERIES_COLORS.length]

  const chartData = {
    datasets: trend.series.map((series, index) => ({
      label: series.sensor_name,
      data: series.readings.map((reading) => ({
        x: reading.recorded_at,
        y: roundTemperature(toDisplayTemperature(reading.recorded_value, localizationContext))
      })),
      borderColor: colorFor(index),
      backgroundColor: colorFor(index),
      borderWidth: 2,
      pointRadius: 0,
      pointHitRadius: 6,
      tension: 0,
      fill: false,
      hidden: hiddenSensorIds.includes(series.sensor_id)
    }))
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    scales: {
      x: {
        type: 'time' as const,
        // Bounds come from the window the server actually queried, so the axis
        // stays fixed to that period rather than drifting with the clock.
        min: moment(trend.starts_at).valueOf(),
        max: moment(trend.ends_at).valueOf(),
        time: { unit: 'hour' as const, displayFormats: { hour: 'HH:mm' } },
        ticks: {
          maxRotation: 0,
          // Tick labels follow the location's timezone, not the browser's.
          callback: (value: number | string) => moment.tz(value, timezone).format('HH:mm')
        },
        grid: { display: false }
      },
      y: {
        ticks: {
          // Hiding lines narrows the axis range, and Chart.js then derives ticks
          // like 4.000000000000001 — round before rendering, and keep the tick
          // count low so a narrow range does not demand absurd precision.
          maxTicksLimit: 8,
          callback: (value: number | string) => `${roundTemperature(value)}${unit}`
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => {
            const timestamp = items[0]?.parsed?.x
            return timestamp == null ? '' : moment.tz(timestamp, timezone).format('ll HH:mm')
          },
          label: (item: TooltipItem<'line'>) => {
            const value = item.parsed?.y
            return value == null ? '' : `${item.dataset.label}: ${value.toFixed(2)}${unit}`
          }
        }
      }
    }
  }

  const subtitle = [
    `${trend.readings_count} readings`,
    `${trend.sensors_count} sensors`,
    `${trend.interval_minutes}-minute intervals`
  ].join(' · ')

  const dayLabel = moment.tz(trend.starts_at, timezone).format('dddd, ll')

  return (
    <div className="sensor-trend-chart">
      <div className="sensor-trend-header">
        <div>
          <div className="sensor-trend-title">Temperature trend</div>
          <div className="sensor-trend-subtitle">{subtitle}</div>
          <div className="sensor-trend-window">
            {dayLabel}
            {isToday && ' (today)'}
          </div>
        </div>

        <div className="sensor-trend-day-picker">
          <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <DesktopDatePicker
              label="Day"
              value={selectedDay}
              maxDate={moment(trend.max_date, DATE_FORMAT)}
              disabled={loading}
              // onChange fires on every keystroke, including half-typed dates, so
              // only the committed value triggers a fetch.
              onChange={(date) => date !== null && setSelectedDay(date)}
              // onAccept covers picking from the calendar; blur covers a date
              // typed into the field, which does not raise onAccept.
              onAccept={loadDay}
              slotProps={{
                textField: {
                  size: 'small',
                  onBlur: () => loadDay(selectedDay)
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#dee2e6' }
                }
              }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-2 mb-0 py-2">{error}</div>}

      {trend.readings_count === 0 ? (
        <div className="sensor-trend-empty">
          No readings on {dayLabel}.
        </div>
      ) : (
        <>
          <div className="sensor-trend-canvas">
            <Line data={chartData} options={options} />
          </div>

          <div className="sensor-trend-legend">
            {trend.series.map((series, index) => {
              const hidden = hiddenSensorIds.includes(series.sensor_id)

              return (
                <button
                  key={series.sensor_id}
                  type="button"
                  className={`sensor-trend-legend-item ${hidden ? 'sensor-trend-legend-item-off' : ''}`}
                  aria-pressed={!hidden}
                  onClick={() => toggleSensor(series.sensor_id)}
                >
                  <span className="sensor-trend-legend-line" style={{ backgroundColor: colorFor(index) }} />
                  {series.sensor_name}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default SensorTrendChart
